import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import React, { Fragment, FunctionComponent, SyntheticEvent, useEffect, useState } from 'react';
import { useAccount } from '../../hooks/useAccount';
import { refreshUserAccount, updateEmail } from '../../services/account';
import * as honeybadger from '../../services/honeybadger';
import { createBodyFromMessage, createMailtoLink } from '../../utils/createMailtoLinks';
import { genericGraphQLErrorIdentifier } from '../error-reporter/error-identifiers/generic-graphql-error-identifier';
import { Input } from '../ui';

const EmailEnforcer: FunctionComponent = () => {
  const { account, loading: loadingAccount } = useAccount();
  const email = account?.email;
  const isAccountLoaded = !!account && !loadingAccount;

  const [showDialog, setShowDialog] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailAddressTouched, setEmailAddressTouched] = useState(false);
  const [validEmailAddress, setValidEmailAddress] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const subject = `Newslit Support Request`;
  const mailtoLink = createMailtoLink('help@newslit.co', subject, createBodyFromMessage('', true));

  useEffect(() => {
    setValidEmailAddress(isValid(emailAddress));
  }, [emailAddress]);

  useEffect(() => {
    const value = email?.emailAddress || '';
    setEmailAddress(value);
  }, [email]);

  useEffect(() => {
    if (isAccountLoaded && !email?.emailAddress) {
      setShowDialog(true);
    } else {
      setShowDialog(false);
    }
  }, [isAccountLoaded, email, setShowDialog]);

  const save = (event: SyntheticEvent) => {
    event.preventDefault();

    if (isValid(emailAddress)) {
      addEmail(emailAddress);
    }
  };

  const addEmail = async (emailAddress: string) => {
    try {
      await updateEmail(emailAddress);
      // Pull the refreshed account (with the new email) into the cache so
      // the dialog closes once the email lands.
      await refreshUserAccount();
    } catch (error: any) {
      console.error(error);
      honeybadger.notify(error, 'addEmail');
      setError(error);
    }
  };

  const getMessageBox = () => {
    if (!error) {
      return;
    }

    return genericGraphQLErrorIdentifier(1, error, error => setError(null));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmailAddress(value);
    setEmailAddressTouched(true);
  };

  const isValid = (value: string) => {
    const regex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(value);
  };

  return (
    <Transition show={showDialog} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        open={showDialog}
        onClose={() => {}}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity backdrop-filter backdrop-blur-xs" />
          </TransitionChild>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">Provide your email</h3>
                <div className="mt-2 max-w-xl text-sm text-gray-500">
                  <p>Change the email address you want associated with your account.</p>
                </div>

                {getMessageBox()}

                <form onSubmit={(e: any) => save(e)} className="space-y-6 mt-3">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <div className="mt-1">
                      <Input
                        type="text"
                        name="email"
                        id="email"
                        required
                        error={
                          emailAddressTouched && !validEmailAddress
                            ? 'Please enter a valid email address'
                            : undefined
                        }
                        value={emailAddress}
                        onChange={(e: any) => handleChange(e)}
                      />
                    </div>
                  </div>

                  <p className="text-sm text-gray-500">
                    Having troubles adding your email?{' '}
                    <a href={mailtoLink} className="text-secondary hover:text-secondary-dark">
                      Contact us
                    </a>
                  </p>

                  <div className="space-y-1">
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        className="mt-3 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-xs font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                        disabled={!isValid(emailAddress)}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EmailEnforcer;
